import styled from 'styled-components';

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.landingSpacing.md};
`;

export const ModalContent = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0px 12px 32px rgba(0, 0, 0, 0.18);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.landingSpacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const ModalTitle = styled.h2`
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1.3;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
`;

export const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.text};
  }
`;

export const ModalBody = styled.div`
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.md};
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.xs};
`;

export const InputLabel = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
`;

export const TextInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.landingSpacing.sm};
  border: 1px solid ${({ theme, $hasError }) => ($hasError ? '#d84c42' : theme.landing.cardBorder)};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.background};
  color: ${({ theme }) => theme.landing.text};
  font-size: 0.95rem;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }

  &::placeholder {
    color: ${({ theme }) => theme.landing.textSecondary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SelectInput = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.landingSpacing.sm};
  border: 1px solid ${({ theme, $hasError }) => ($hasError ? '#d84c42' : theme.landing.cardBorder)};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.background};
  color: ${({ theme }) => theme.landing.text};
  font-size: 0.95rem;
  line-height: 1.4;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  appearance: none;

  &:focus {
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  font-weight: 400;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #d84c42;
`;

export const ModalCancelButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
  }
`;

export const ModalSubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.buttonHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* ── File picker (siteLogo, image only) ─────────────────────────────────── */

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FilePickerButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  width: 100%;
  border: 1px dashed ${({ theme, $hasError }) => ($hasError ? '#d84c42' : theme.landing.cardBorder)};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.background};
  color: ${({ theme }) => theme.landing.textSecondary};
  padding: ${({ theme }) => theme.landingSpacing.sm};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.landing.buttonBg};
    color: ${({ theme }) => theme.landing.text};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const FilePreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  margin-top: ${({ theme }) => theme.landingSpacing.xs};
`;

export const ImagePreview = styled.img`
  width: 100px;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const FileNameText = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.landing.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: #d84c42;
  }
`;
